// Die Akte der Anlage (E3, S5; Mockup /crm/anlage/:id) – alles zu einer
// Anlage an einem Ort, von oben nach unten:
//   - seit E3c (S2) die Leiste: die acht Schritte der Kette, was erledigt ist
//     und was fehlt (Leiste.tsx) – an Stelle des Wegs durch die acht Kisten;
//   - offene Fragen an diesem Projekt (das Band in Lime, nicht zu übersehen,
//     mit dem Sprung zur ältesten);
//   - drei Karten: Kunde und Objekt – je mit „ändern“ (berichtigen, Gernot
//     20.10.) – und der Kern als Baum (Happy Energy → Firma → Kunde →
//     Anlage → Unterlagen);
//   - die Termine der Anlage: Montagetermin, „Auftrag erteilt am“, „montiert
//     am“ (Termine.tsx);
//   - seit E3c Bestellung, Dachpartie und Bodenpartie – je eine Karte mit dem
//     Stand aus der Kette (S2), seit S3 und S4 mit allem, was dort zu tun ist:
//     Lieferant, Liefertermin, Module, die Mail als Vorschau und zum Öffnen,
//     „Bestellt“; die Partien mit Tag, Uhrzeit, Dach-Unterlage, „Beauftragt“
//     und „Eingeteilt“ (../ablauf/);
//   - die Unterlagen: Aufnahme vor Ort, Dachplanung (seit dem Augentest
//     21.10.2026 mit „Ansehen“ im Browser – bis 23.10. „Öffnen“ – und „Herunterladen“ mit lesbarem
//     Namen), Materialliste, Unterlage für die Montage, dazu was später
//     kommt (gestrichelt);
//   - die Fragen (seit S6): alle Fragen dieser Anlage und ihrer
//     Besichtigungen, offene zuerst – je mit „woran“ und dem Weg dorthin,
//     antworten und „erledigt“ gleich hier; darunter „Frage stellen“ zur
//     Anlage (das ganze Projekt, Grundsatz 9) – im selben Muster wie
//     überall (../fragen/FragenAmStueck.tsx);
//   - der Verlauf, lesbar: wann – was – wer (Gerät), neueste zuerst;
//   - seit E3c (S6; O97), nur für die Leitung, ganz unten und leise: „Anlage
//     löschen“ – ein Satz, was verschwindet (mit oder ohne Kunde), der Name
//     des Kunden zum Eintippen, dann endgültig (LoeschenKarte).
//
// Adresse: #/anlage/<kennung> (Recht „kunden“: Leitung und Büro) – seit
// E3c, S2 auch #/anlage/<kennung>/<stelle> (termin, bestellung, dachpartie,
// bodenpartie): die Knöpfe in „Zu tun“ springen gleich dorthin.

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  darf,
  kundeBerichtigenEingabe,
  objektBerichtigenEingabe,
  PRODUKTLINIEN,
  type Akte,
  type AkteAufnahme,
  type AkteVerlaufEintrag,
  type AnlageLoeschenAntwort,
  type FrageMitKontext,
} from "@happy-energy/kern";
import { Chip, Ikon, senden, toastZeigen, type IkonName } from "@happy-energy/oberflaeche";
import { meldungAus } from "../meldung.ts";
import { AblaufKarten } from "../ablauf/AblaufKarten.tsx";
import { k2ExcelDateiname } from "../ablauf/ablauf.ts";
import { K2ExcelKnopf } from "../ablauf/gemeinsam.tsx";
import { adresseZeigen, dateiAdresse, kalendertagZeigen, objektArtName, standArt, tagZeigen, wannZeigen } from "../anzeige.ts";
import { StandChip } from "../bausteine.tsx";
import { FrageKarte, FrageStellen } from "../fragen/FragenAmStueck.tsx";
import { FragenBand } from "../fragen/FragenBand.tsx";
import { aeltesteZuerst, anzahl, offeneZuerst, ortText } from "../fragen/fragen.ts";
import { frageMerken, frageZeigen } from "../fragen/sprung.ts";
import { FrageZeichen } from "../fragen/zeichen.tsx";
import { DachplanungKnoepfe, dateiRahmen } from "../freigabe/DachplanungKarte.tsx";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { nurEigene, type AkteStelle } from "../navigation.ts";
import { Seite } from "../rahmen.tsx";
import { anderenFensternMelden, useAmServerGeaendert } from "../speicher.ts";
import { verlaufZeit } from "../tage.ts";
import { useCrm } from "../zusammenhang.tsx";
import { besichtigungZahlen, dachplanung, ersteSendung, namenZumAendern, produktlinieKurz } from "./akte.ts";
import { Berichtigen } from "./Berichtigen.tsx";
import { Leiste } from "./Leiste.tsx";
import { TermineKarte } from "./Termine.tsx";
import { verlaufArt, verlaufText, verlaufWer } from "./verlauf.ts";
import "../formular.css";
import "./akte.css";

/** Nach dem Berichtigen: Die Doku-App trägt Name und Adresse als Kopie und zieht sie beim nächsten Abgleich nach (PLAN.md, E3, „Handwerk“). */
const NACH_DEM_BERICHTIGEN = "Steht jetzt auch in der Doku-App, sobald sie abgleicht.";

// --- Kunde, Objekt, Kern -----------------------------------------------------------------------

function KundeKarte({ akte, neuLaden }: { akte: Akte; neuLaden: () => void }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [berichtigt, setBerichtigt] = useState(false);
  const k = akte.kunde;
  return (
    <section className="crm-karte akte-karte" aria-label="Kunde">
      <div className="karten-kopf">
        <Ikon name="nutzer" />
        <h2>Kunde</h2>
        {!bearbeiten && (
          <button type="button" className="crm-knopf rand klein" onClick={() => {
              setBearbeiten(true);
              setBerichtigt(false);
            }}>
            <Ikon name="stift" groesse="k" /> ändern
          </button>
        )}
      </div>
      {bearbeiten ? (
        <Berichtigen
          titel="Kunde berichtigen"
          // Seit E3b (O82): Vor- und Nachname getrennt – der Name in den Listen wird daraus („Vorname Nachname“).
          felder={[
            { schluessel: "vorname", name: "Vorname", freiwillig: true, autoComplete: "off" },
            { schluessel: "nachname", name: "Nachname", autoComplete: "off" },
            { schluessel: "telefon", name: "Telefon", typ: "tel", freiwillig: true },
            { schluessel: "email", name: "E-Mail", typ: "email", freiwillig: true },
          ]}
          anfang={{ ...namenZumAendern(k), telefon: k.telefon ?? "", email: k.email ?? "" }}
          // Gespeichert wird, was in den Feldern steht – bei ungeteilten Kunden vorbefüllt mit dem Vorschlag (akte.ts, namenZumAendern).
          vertrag={kundeBerichtigenEingabe}
          pfad={`/api/crm/kunde/${k.id}`}
          abbrechen={() => setBearbeiten(false)}
          fertig={() => {
            setBearbeiten(false);
            setBerichtigt(true);
            toastZeigen("Kunde berichtigt");
            neuLaden();
          }}
        />
      ) : (
        <>
          <div>
            <b className="akte-gross">{k.name}</b>
            <div className="leise klein">Person</div>
          </div>
          <div className="akte-kontakt">
            {k.telefon ? (
              <a href={`tel:${k.telefon.replace(/[^\d+]/g, "")}`}>
                <Ikon name="telefon" groesse="k" /> {k.telefon}
              </a>
            ) : (
              <span className="leise">
                <Ikon name="telefon" groesse="k" /> keine Telefonnummer
              </span>
            )}
            {k.email ? (
              <a href={`mailto:${k.email}`}>
                <Ikon name="senden" groesse="k" /> {k.email}
              </a>
            ) : (
              <span className="leise">
                <Ikon name="senden" groesse="k" /> keine E-Mail
              </span>
            )}
          </div>
          {berichtigt && (
            <p className="crm-ok-satz">
              <Ikon name="haken" groesse="k" /> {NACH_DEM_BERICHTIGEN}
            </p>
          )}
          <div className="chip-zeile">
            <Chip art="sp" ikon="uhr">
              kommt später aus dem Lead
            </Chip>
          </div>
        </>
      )}
    </section>
  );
}

function ObjektKarte({ akte, neuLaden }: { akte: Akte; neuLaden: () => void }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [berichtigt, setBerichtigt] = useState(false);
  const o = akte.objekt;
  return (
    <section className="crm-karte akte-karte" aria-label="Objekt">
      <div className="karten-kopf">
        <Ikon name="haus" />
        <h2>Objekt</h2>
        {!bearbeiten && (
          <button type="button" className="crm-knopf rand klein" onClick={() => {
              setBearbeiten(true);
              setBerichtigt(false);
            }}>
            <Ikon name="stift" groesse="k" /> ändern
          </button>
        )}
      </div>
      {bearbeiten ? (
        <Berichtigen
          titel="Adresse berichtigen"
          felder={[
            { schluessel: "strasse", name: "Straße und Hausnummer" },
            { schluessel: "plz", name: "PLZ", schmal: true },
            { schluessel: "ort", name: "Ort" },
          ]}
          anfang={{ strasse: o.strasse, plz: o.plz, ort: o.ort }}
          vertrag={objektBerichtigenEingabe}
          pfad={`/api/crm/objekt/${o.id}`}
          abbrechen={() => setBearbeiten(false)}
          fertig={() => {
            setBearbeiten(false);
            setBerichtigt(true);
            toastZeigen("Adresse berichtigt");
            neuLaden();
          }}
        />
      ) : (
        <>
          <div>
            <b className="akte-gross">{objektArtName(o.art)}</b>
            <div className="akte-adresse">
              {o.strasse}
              <br />
              {o.plz} {o.ort}
            </div>
          </div>
          {berichtigt && (
            <p className="crm-ok-satz">
              <Ikon name="haken" groesse="k" /> {NACH_DEM_BERICHTIGEN}
            </p>
          )}
          <div className="chip-zeile">
            <Chip art="sp" ikon="uhr">
              Hausdaten aus dem Rechner – Stufe 3
            </Chip>
          </div>
        </>
      )}
    </section>
  );
}

/** Der gemeinsame Kern als Baum: Happy Energy, darunter die Firma, darunter Kunde und Objekt, darunter die Anlage, darunter die Unterlagen. */
function KernKarte({ akte }: { akte: Akte }) {
  const { ich } = useCrm();
  const produkt = PRODUKTLINIEN.find((p) => p.schluessel === akte.anlage.produktlinie)?.name ?? akte.anlage.produktlinie;
  return (
    <section className="crm-karte akte-karte" aria-label="Der Kern">
      <div className="karten-kopf">
        <Ikon name="ebenen" />
        <h2>Der Kern</h2>
      </div>
      <ul className="kern-baum">
        <li>
          <i /> Happy Energy – die Marke
        </li>
        <li className="e1">
          <i /> {ich.firma.name} – die Firma
        </li>
        <li className="e2">
          <i /> {akte.kunde.name} · {akte.objekt.ort}
        </li>
        <li className="e3">
          <i /> Anlage {produkt}
        </li>
        <li className="e4">
          <i className="tuerkis" /> Unterlagen
        </li>
      </ul>
      <div className="chip-zeile">
        {PRODUKTLINIEN.map((p) =>
          p.schluessel === akte.anlage.produktlinie ? (
            <Chip key={p.schluessel} art="lime" ikon="sonne">
              {produktlinieKurz(p.schluessel)}
            </Chip>
          ) : p.aktiv ? (
            <Chip key={p.schluessel}>{produktlinieKurz(p.schluessel)}</Chip>
          ) : (
            <Chip key={p.schluessel} art="sp">
              {produktlinieKurz(p.schluessel)} – Stufe 3
            </Chip>
          ),
        )}
      </div>
    </section>
  );
}

// --- Unterlagen -------------------------------------------------------------------------------

function Unterlage({ ikon, titel, text, spaeter = false, children }: { ikon: IkonName; titel: string; text: ReactNode; spaeter?: boolean; children?: ReactNode }) {
  return (
    <li className={`akte-unterlage${spaeter ? " sp" : ""}`}>
      <span className="symbol">
        <Ikon name={ikon} />
      </span>
      <div className="wort">
        <b>{titel}</b>
        <span>{text}</span>
      </div>
      {children && <div className="akte-unterlage-rechts">{children}</div>}
    </li>
  );
}

function AufnahmeUnterlage({ a, mehrere }: { a: AkteAufnahme; mehrere: boolean }) {
  const { ich } = useCrm();
  // Derselbe Chip wie in „Zu prüfen“ und im Weg (seit 22.10.2026, vorher „zu prüfen“): Nachtrag oder nicht, rechnet hier aus den
  // Nachträgen – wie der Server (crm/lesen.ts, hinweisFuer). „Bitte ansehen“ kennt die Akte nicht; das steht in der Freigabe.
  const hinweis = a.stand === "zu_pruefen" && a.nachtraege > 0 ? "nachtrag" : null;
  return (
    <Unterlage
      ikon="kamera"
      titel={mehrere ? `Aufnahme vor Ort · begonnen ${tagZeigen(a.begonnenAm)}` : "Aufnahme vor Ort"}
      text={
        <>
          {besichtigungZahlen(a)}
          {a.nachtraege > 0 && ` · ${a.nachtraege === 1 ? "1 Nachtrag" : `${a.nachtraege} Nachträge`}`} – {a.verkaeufer.name}, gesendet {wannZeigen(a.gesendetAm)}
        </>
      }
    >
      <StandChip stand={standArt({ stand: a.stand, hinweis }, !nurEigene(ich.nutzer.rolle))} />
      <a className="crm-knopf rand klein" href={`#/freigabe/${a.id}`}>
        Öffnen <Ikon name="rechts" groesse="k" />
      </a>
    </Unterlage>
  );
}

function UnterlagenKarte({ akte, neuLaden }: { akte: Akte; neuLaden: () => void }) {
  const { ich } = useCrm();
  const rolle = ich.nutzer.rolle;
  const freigegeben = akte.aufnahmen.find((a) => a.stand === "freigegeben") ?? null;
  const zuPruefen = akte.aufnahmen.find((a) => a.stand === "zu_pruefen") ?? null;
  const k2 = dachplanung(akte.anlage.k2, ersteSendung(akte.aufnahmen));
  const termin = akte.anlage.montagetermin;

  return (
    <section className="crm-karte" aria-label="Unterlagen">
      <div className="karten-kopf">
        <Ikon name="datei" />
        <h2>Unterlagen</h2>
      </div>
      <ul className="akte-unterlagen">
        {akte.aufnahmen.length === 0 ? (
          <Unterlage ikon="kamera" titel="Aufnahme vor Ort" text="Noch keine gesendete Besichtigung – sie erscheint hier, sobald der Verkauf „An das Büro senden“ tippt." />
        ) : (
          akte.aufnahmen.map((a) => <AufnahmeUnterlage key={a.id} a={a} mehrere={akte.aufnahmen.length > 1} />)
        )}

        <Unterlage
          ikon="dach"
          titel="Dachplanung mit K2"
          text={
            <>
              {k2.satz}
              {k2.geaendertAm && <span className="akte-geaendert"> · geändert am {tagZeigen(k2.geaendertAm)}</span>}
            </>
          }
        >
          {akte.anlage.k2.pruefsumme !== null ? (
            <>
              <Chip art="ok" ikon="haken">
                abgelegt
              </Chip>
              <DachplanungKnoepfe
                pruefsumme={akte.anlage.k2.pruefsumme}
                kundeName={akte.kunde.name}
                // Der Tag der ersten Besichtigung der Anlage (seit E3b, O76).
                rahmen={dateiRahmen(akte.aufnahmen.map((x) => x.begonnenAm).sort()[0], akte.anlage.produktlinie)}
              />
            </>
          ) : k2.stand === "uebersprungen" ? (
            <Chip>übersprungen</Chip>
          ) : (
            <Chip ikon="uhr">offen</Chip>
          )}
          <Chip art="sp" ikon="uhr">
            Module auslesen – Dezember
          </Chip>
        </Unterlage>

        {/* Seit E3c (S3): der Excel-Report von K2 – die Unterkonstruktionsliste für den Lieferanten. */}
        <Unterlage
          ikon="liste"
          titel="K2-Liste (Excel)"
          text={akte.ablauf.k2Excel ? "Unterkonstruktionsliste aus K2 – geht mit der Bestellung an den Lieferanten" : "noch keine – der Excel-Report von K2 (.xlsx) für die Bestellung"}
        >
          {akte.ablauf.k2Excel ? (
            <>
              <Chip art="ok" ikon="haken">
                abgelegt
              </Chip>
              <a
                className="crm-knopf rand klein"
                href={dateiAdresse(akte.ablauf.k2Excel)}
                download={k2ExcelDateiname(akte.kunde.name, dateiRahmen(akte.aufnahmen.map((x) => x.begonnenAm).sort()[0], akte.anlage.produktlinie))}
              >
                Herunterladen
              </a>
            </>
          ) : (
            <Chip ikon="uhr">offen</Chip>
          )}
          <K2ExcelKnopf anlageId={akte.anlage.id} da={akte.ablauf.k2Excel !== null} fertig={neuLaden} />
        </Unterlage>

        <Unterlage
          ikon="paket"
          titel="Materialliste"
          text={akte.materialFreigegeben ? "freigegeben – kommt auf die Unterlage für die Montage" : zuPruefen ? "Entwurf – wartet auf die Freigabe" : "entsteht mit der Freigabe"}
        >
          {akte.materialFreigegeben && freigegeben && darf(rolle, "freigeben") && (
            <a className="crm-knopf rand klein" href={`#/freigabe/${freigegeben.id}`}>
              Ansehen <Ikon name="rechts" groesse="k" />
            </a>
          )}
          {!akte.materialFreigegeben && zuPruefen && darf(rolle, "freigeben") && (
            <a className="crm-knopf lime klein" href={`#/freigabe/${zuPruefen.id}`}>
              Zur Freigabe <Ikon name="rechts" groesse="k" />
            </a>
          )}
        </Unterlage>

        <Unterlage
          ikon="drucker"
          titel="Unterlage für die Montage"
          text={
            freigegeben
              ? `zum Drucken oder als PDF sichern${termin.am ? ` · Montage ${kalendertagZeigen(termin.am)}` : ""}`
              : "entsteht mit der Freigabe – Fotos mit Zeichnung, Notizen, Längen, Material"
          }
        >
          {freigegeben && darf(rolle, "unterlagen") && (
            <a className="crm-knopf rand klein" href={`#/unterlage/${freigegeben.id}`}>
              Öffnen <Ikon name="rechts" groesse="k" />
            </a>
          )}
        </Unterlage>

        <Unterlage spaeter ikon="diagramm" titel="Investitionsrechnung" text="aus dem Rechner, wenn er im System ist">
          <Chip art="sp" ikon="uhr">
            Stufe 3
          </Chip>
        </Unterlage>
        <Unterlage spaeter ikon="unterschrift" titel="Unterschrift zur Dachplanung" text="die Kundin bestätigt ihre Anlage">
          <Chip art="sp" ikon="uhr">
            Stufe 2
          </Chip>
        </Unterlage>
        <Unterlage spaeter ikon="drohne" titel="Drohnenfoto und Drohnenvideo" text="vom Verkauf und von der Inbetriebnahme">
          <Chip art="sp" ikon="uhr">
            Stufe 2
          </Chip>
        </Unterlage>
        <Unterlage spaeter ikon="schild" titel="Prüfprotokoll" text="von selbst aus der Inbetriebnahme">
          <Chip art="sp" ikon="uhr">
            Kiste Inbetriebnahme
          </Chip>
        </Unterlage>
        <Unterlage spaeter ikon="video" titel="Dankes-Video" text="drei Tage nach der Inbetriebnahme, per WhatsApp">
          <Chip art="sp" ikon="uhr">
            Kiste Nachsorge
          </Chip>
        </Unterlage>
        <Unterlage spaeter ikon="liste" titel="Angebot und Rechnung" text="aus dem Rechner und der Materialliste – mit Preisen">
          <Chip art="sp" ikon="uhr">
            später
          </Chip>
        </Unterlage>
      </ul>
    </section>
  );
}

// --- Fragen ----------------------------------------------------------------------------------------

/** Woran die Frage hängt – und der Weg dorthin: die Besichtigung, an der Stelle der Frage (../fragen/sprung.ts). */
function FrageWoran({ f }: { f: FrageMitKontext }) {
  return (
    <>
      <span>{ortText(f.bezug.tabelle, f.kontext.stationNr)}</span>
      <a href={`#/freigabe/${f.aufnahmeId}`} onClick={() => frageMerken(f.aufnahmeId, f.id)}>
        in der Besichtigung ansehen <Ikon name="rechts" groesse="k" />
      </a>
    </>
  );
}

/**
 * Alle Fragen dieser Anlage und ihrer Besichtigungen – offene zuerst, jede
 * zum Antworten und „erledigt“ setzen; darunter „Frage stellen“ zur Anlage.
 * Eine Frage hängt am Server immer auch an einer Besichtigung (für die
 * Listen und das Löschen mit der Aufnahme) – eine zur Anlage hier an der
 * jüngsten gesendeten. Ohne Erklär-Absatz (Augentest 21.10.2026).
 */
function FragenKarte({ akte, neuLaden }: { akte: Akte; neuLaden: () => void }) {
  const geordnet = offeneZuerst(akte.fragen);
  const juengste = akte.aufnahmen[0] ?? null;
  return (
    <section className="crm-karte akte-karte" id="akte-fragen" aria-label="Fragen">
      <div className="karten-kopf">
        <FrageZeichen />
        <h2>Fragen</h2>
        {akte.fragen.length > 0 && <span className="leise klein">{akte.fragen.filter((f) => f.stand === "offen").length} offen</span>}
      </div>
      {geordnet.length === 0 ? (
        <p className="leise klein akte-leer">Noch keine Fragen.</p>
      ) : (
        <div className="akte-fragen-liste">
          {geordnet.map((f) => (
            <FrageKarte key={f.id} frage={f} neuLaden={neuLaden} zusammenhang={<FrageWoran f={f} />} />
          ))}
        </div>
      )}
      {juengste ? (
        <FrageStellen aufnahmeId={juengste.id} bezug={{ tabelle: "anlage", id: akte.anlage.id }} wo="zur Anlage" neuLaden={neuLaden} />
      ) : (
        <p className="leise klein akte-leer">Fragen gibt es, sobald eine Besichtigung im Büro ist.</p>
      )}
    </section>
  );
}

// --- Verlauf ---------------------------------------------------------------------------------

/** So viele Einträge zuerst – der Rest mit einem Klick. */
const VERLAUF_ZUERST = 25;

function VerlaufKarte({ verlauf }: { verlauf: readonly AkteVerlaufEintrag[] }) {
  const [alle, setAlle] = useState(false);
  const sichtbar = alle ? verlauf : verlauf.slice(0, VERLAUF_ZUERST);
  return (
    <section className="crm-karte" aria-label="Verlauf">
      <div className="karten-kopf">
        <Ikon name="uhr" />
        <h2>Verlauf</h2>
        <span className="leise klein">wer hat wann was getan – neueste zuerst</span>
      </div>
      {verlauf.length === 0 ? (
        <p className="leise klein akte-leer">Noch nichts vermerkt.</p>
      ) : (
        <ol className="verlauf">
          {sichtbar.map((e, i) => (
            <li key={`${e.wann}-${e.was}-${e.tabelle}-${e.datensatzId ?? ""}-${i}`} className={`verlauf-zeile ${verlaufArt(e)}`}>
              <span className="verlauf-wann">{verlaufZeit(e.wann)}</span>
              <b className="verlauf-was">{verlaufText(e)}</b>
              <span className="verlauf-wer">{verlaufWer(e)}</span>
            </li>
          ))}
        </ol>
      )}
      {!alle && verlauf.length > VERLAUF_ZUERST && (
        <div>
          <button type="button" className="crm-knopf rand klein" onClick={() => setAlle(true)}>
            Alle {verlauf.length} Einträge zeigen
          </button>
        </div>
      )}
      <p className="leise klein akte-leer">Im Verlauf stehen nur Zeitpunkt, Art und wer – nie Inhalte. Was an Fotos und Notizen geschah, zeigt die Besichtigung selbst.</p>
    </section>
  );
}

// --- Anlage löschen (seit E3c, S6; O97 – nur die Leitung) ------------------------------------------

/** Der Satz in der Rückfrage: was verschwindet – und ob der Kunde mitgeht (Akte.loeschen, dieselbe Regel wie am Server). */
export function loeschSatz(akte: Pick<Akte, "kunde" | "loeschen">): string {
  const was = "Die Anlage samt Besichtigung, Fotos, Memos, Fragen und Bestellungen.";
  const { kundeBleibt, objektBleibt } = akte.loeschen;
  if (!kundeBleibt) return `${was} Der Kunde ${akte.kunde.name} verschwindet mit, weil er keine andere Anlage hat.`;
  if (objektBleibt) return `${was} Der Kunde ${akte.kunde.name} bleibt, er hat noch eine Anlage an dieser Adresse.`;
  return `${was} Der Kunde ${akte.kunde.name} bleibt, er hat noch ein anderes Objekt.`;
}

/**
 * Ganz unten, leise: „Anlage löschen …“. Erst dann die Karte: der Satz, was
 * verschwindet, der Name des Kunden zum Eintippen – genau so, wie er oben
 * steht; stimmt er nicht, passiert nichts (der Knopf bleibt aus, der Server
 * sagt ohnehin 409 und verrät nichts). Danach zurück zu „Kunden & Anlagen“.
 * Was die Doku-App damit tut, steht in apps/doku/src/wegraeumen.ts: Sie
 * räumt ihre Kopie beim nächsten Abgleich weg.
 */
function LoeschenKarte({ akte }: { akte: Akte }) {
  const [offen, setOffen] = useState(false);
  const [eingabe, setEingabe] = useState("");
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const name = akte.kunde.name;
  const passt = eingabe.trim() === name;

  async function loeschen(e: FormEvent) {
    e.preventDefault();
    if (!passt || sendet) return;
    setSendet(true);
    setMeldung(null);
    try {
      const antwort = await senden<AnlageLoeschenAntwort>(`/api/crm/anlage/${akte.anlage.id}/loeschen`, { name: eingabe.trim() });
      toastZeigen(antwort.ok && antwort.kundeWeg ? "Anlage gelöscht – der Kunde mit ihr" : "Anlage gelöscht – der Kunde bleibt");
      anderenFensternMelden(akte.anlage.id);
      window.location.hash = "#/kunden";
    } catch (f) {
      setMeldung(meldungAus(f, "Löschen darf nur die Leitung."));
      setSendet(false);
    }
  }

  if (!offen) {
    return (
      <div className="akte-loeschen-leise">
        <button type="button" className="crm-knopf leise klein" onClick={() => setOffen(true)}>
          Anlage löschen …
        </button>
      </div>
    );
  }
  return (
    <section className="crm-karte akte-karte akte-loeschen" aria-label="Anlage löschen">
      <div className="karten-kopf">
        <Ikon name="achtung" />
        <h2>Anlage löschen</h2>
      </div>
      <p className="akte-loeschen-satz">
        <b>Das verschwindet endgültig:</b> {loeschSatz(akte)}
      </p>
      <form className="akte-loeschen-form" onSubmit={(e) => void loeschen(e)}>
        <label>
          <span>
            Zum Bestätigen den Namen des Kunden eintippen: <b>{name}</b>
          </span>
          <input type="text" value={eingabe} autoComplete="off" spellCheck={false} onChange={(e) => setEingabe(e.target.value)} />
        </label>
        {meldung && (
          <p className="crm-fehler-satz" role="alert">
            {meldung}
          </p>
        )}
        <div className="akte-loeschen-knoepfe">
          <button type="submit" className="crm-knopf rot" disabled={!passt || sendet}>
            <Ikon name="achtung" groesse="k" /> Endgültig löschen
          </button>
          <button
            type="button"
            className="crm-knopf rand"
            disabled={sendet}
            onClick={() => {
              setOffen(false);
              setEingabe("");
              setMeldung(null);
            }}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}

// --- Die Seite ---------------------------------------------------------------------------------

/**
 * Seit E3c, S2: Kommt die Akte aus „Zu tun“ (#/anlage/<id>/<stelle>), springt
 * sie einmal dorthin, sobald sie geladen ist – nicht bei jedem Neuladen.
 */
function useZurStelle(stelle: AkteStelle | undefined, geladen: boolean): void {
  const gesprungen = useRef<string | null>(null);
  useEffect(() => {
    if (!stelle || !geladen || gesprungen.current === stelle) return;
    gesprungen.current = stelle;
    document.getElementById(`akte-${stelle}`)?.scrollIntoView({ block: "start" });
  }, [stelle, geladen]);
}

export function AkteSeite({ id, stelle }: { id: string; stelle?: AkteStelle }) {
  const { ich } = useCrm();
  const { daten: akte, fehler, neu } = useLaden<Akte>(`/api/crm/anlage/${id}`);
  // Eine Frage oder Antwort – hier, auf einer anderen Seite oder in einem zweiten Fenster: neu laden (seit S6).
  useAmServerGeaendert(neu);
  useZurStelle(stelle, akte !== undefined);
  const offen = akte ? aeltesteZuerst(akte.fragen.filter((f) => f.stand === "offen")) : [];
  const erste = offen[0];
  // Die Fragen dieser Anlage zeigt die Akte selbst – das Band oben nennt nur die übrigen.
  const hier = akte ? [...new Set([...akte.aufnahmen.map((a) => a.id), ...akte.fragen.map((f) => f.aufnahmeId)])] : undefined;

  return (
    <Seite
      {...(hier ? { fragenHier: hier } : {})}
      titel={akte ? akte.kunde.name : "Akte der Anlage"}
      unter={akte ? `Akte der Anlage · ${adresseZeigen(akte.objekt)}` : "Kunde · Objekt · Anlage · Unterlagen"}
      knoepfe={
        <a href="#/kunden" className="crm-knopf rand klein">
          <Ikon name="links" groesse="k" /> Kunden & Anlagen
        </a>
      }
    >
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!akte && fehler === undefined && <Laedt text="Die Akte wird geladen …" />}
      {akte && (
        <>
          <Leiste kette={akte.kette} />
          {erste && (
            <FragenBand
              titel={`${anzahl(offen.length, "1 offene Frage", "# offene Fragen")} an diesem Projekt`}
              unter={`Die älteste: ${ortText(erste.bezug.tabelle, erste.kontext.stationNr)} – ${erste.von.name}, ${wannZeigen(erste.gestelltAm)}. Antworten gleich hier unten bei den Fragen.`}
              knopf={{ name: offen.length === 1 ? "Zur Frage" : "Zur ersten", los: () => frageZeigen(erste.id) }}
            />
          )}
          <div className="akte-raster">
            <KundeKarte akte={akte} neuLaden={neu} />
            <ObjektKarte akte={akte} neuLaden={neu} />
            <KernKarte akte={akte} />
          </div>
          <TermineKarte anlage={akte.anlage} gespeichert={neu} />
          <AblaufKarten akte={akte} stelle={stelle} neuLaden={neu} />
          <UnterlagenKarte akte={akte} neuLaden={neu} />
          <FragenKarte akte={akte} neuLaden={neu} />
          <VerlaufKarte verlauf={akte.verlauf} />
          {darf(ich.nutzer.rolle, "anlage-loeschen") && <LoeschenKarte akte={akte} />}
        </>
      )}
    </Seite>
  );
}
